import { Suspense, lazy, useCallback } from "react";
import {
  BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate, useSearchParams,
} from "react-router-dom";
import { LocaleProvider } from "@/i18n/LocaleProvider";
import { AuthProvider } from "@/auth/AuthProvider";
import { setReturnPath } from "@/auth/session";
import { Layout } from "@/components/Layout";
import { NotFound } from "@/pages/NotFound";
import { MarketProvider } from "@/v3/MarketProvider";
import { MARKET_SLUGS } from "@/v3/markets";
import { ShellV3 } from "@/v3/Shell";
import { HomeV3 } from "@/v3/pages/Home";
import { ContentPage } from "@/v3/pages/ContentPage";

const ResultsV3 = lazy(() => import("@/v3/pages/Results").then((m) => ({ default: m.ResultsV3 })));
const ListingDetailV3 = lazy(() => import("@/v3/pages/ListingDetail").then((m) => ({ default: m.ListingDetailV3 })));
/* WIZARD-2 (v4 package) replaced the v3 six-step wizard, deleted 2026-11-03
   (src/v3/pages/ListProperty.tsx). Order: docs/spec/WIZARD-2-ORDER.md. */
const ListPropertyV4 = lazy(() => import("@/v3/pages/ListPropertyV4").then((m) => ({ default: m.ListPropertyV4 })));
const ListPropertySuccess = lazy(() => import("@/v3/wizard/Success").then((m) => ({ default: m.ListPropertySuccess })));
const PlansV3 = lazy(() => import("@/v3/pages/Plans").then((m) => ({ default: m.PlansV3 })));
const MyListingsV3 = lazy(() => import("@/v3/pages/MyListings").then((m) => ({ default: m.MyListingsV3 }))); // L1b
const DashboardV2 = lazy(() => import("@/v3/pages/DashboardV2").then((m) => ({ default: m.DashboardV2 }))); // L1b
const InboxV3 = lazy(() => import("@/v3/pages/Inbox").then((m) => ({ default: m.InboxV3 })));
const AccountProfileV3 = lazy(() => import("@/v3/pages/AccountProfile").then((m) => ({ default: m.AccountProfileV3 })));
const PublicProfileV3 = lazy(() => import("@/v3/pages/PublicProfile").then((m) => ({ default: m.PublicProfileV3 })));

/* The console and the agent panel are their own chunks: neither is reachable
 * by an anonymous visitor, so neither belongs in the bundle every visitor
 * downloads. Same reason Leaflet and the geo fixture are split. */
/* Only the homepage ships in the entry chunk. Every other route is fetched
 * when it is first opened, which is the single largest cheap win available:
 * a first-time visitor landing on "/" was downloading the listing detail,
 * both auth pages, the panel, pricing and checkout before seeing anything. */
const Login = lazy(() => import("@/pages/Login").then((m) => ({ default: m.Login })));
const Register = lazy(() => import("@/pages/Register").then((m) => ({ default: m.Register })));
const Account = lazy(() => import("@/pages/Account").then((m) => ({ default: m.Account })));
const Checkout = lazy(() => import("@/pages/Checkout").then((m) => ({ default: m.Checkout })));
const Privacy = lazy(() => import("@/pages/Privacy").then((m) => ({ default: m.Privacy })));
const Admin = lazy(() => import("@/pages/Admin").then((m) => ({ default: m.Admin })));
const Agent = lazy(() => import("@/pages/Agent").then((m) => ({ default: m.Agent })));

/* Legacy deep link: ?listing=<id> on "/" opened a listing on the old site.
 * Those URLs are in the wild, so they redirect rather than 404. */
function HomeOrDeepLink() {
  const [params] = useSearchParams();
  const listingId = params.get("listing");
  if (listingId) return <Navigate to={`/listing/${encodeURIComponent(listingId)}`} replace />;
  return <ShellV3><HomeV3 /></ShellV3>;
}

/* THE SITE, ONE ROUTE TABLE, MOUNTED ONCE PER MARKET PREFIX.
 *
 * Paths here are RELATIVE, so the same table serves "/", "/eg/…" and "/sa/…"
 * without being written three times and without three chances to forget a
 * route in one of them. React Router resolves them against whichever parent
 * matched. MARKET_SLUGS is the manifest those parents are generated from, and
 * scripts/verify-market-slugs.mjs fails the gate if it and `countries.slug`
 * ever disagree.
 *
 * TWO SHELLS, ON PURPOSE. A screen that has been rebuilt renders inside
 * ShellV3, which carries the design's header and footer and the `.sq-v3`
 * wrapper that switches the design's stylesheet on. A screen that has not
 * renders inside Layout, exactly as before. They coexist so the rebuild can
 * ship a screen at a time; when the last screen moves, Layout goes.
 */
function SiteRoutes() {
  return (
    <Suspense fallback={null}>
      <Routes>
        {/* ── rebuilt to the v3 design ───────────────────────────────── */}
        <Route path="" element={<HomeOrDeepLink />} />
        <Route path="p/:slug" element={<ShellV3><ContentPage /></ShellV3>} />
        <Route path="search" element={<ShellV3><ResultsV3 /></ShellV3>} />
        <Route path="listing/:id" element={<ShellV3><ListingDetailV3 /></ShellV3>} />
        <Route path="list-property" element={<ShellV3><ListPropertyV4 /></ShellV3>} />
        <Route path="list-property/success/:id" element={<ShellV3><ListPropertySuccess /></ShellV3>} />
        {/* /plans is the design's URL; /pricing is the one in the wild and
            keeps working rather than 404ing on a link somebody shared. */}
        <Route path="plans" element={<ShellV3><PlansV3 /></ShellV3>} />
        <Route path="pricing" element={<ShellV3><PlansV3 /></ShellV3>} />

        {/* KAR-136: the member sign-in and register wear the site's shell. The
            FORMS are unchanged; only the header and footer around them are. */}
        <Route path="login" element={<ShellV3><Login /></ShellV3>} />
        <Route path="register" element={<ShellV3><Register /></ShellV3>} />

        {/* ── not yet rebuilt: the previous shell, untouched ─────────── */}
        {/* The account surface. `/account` is the design's dashboard;
            `/account/panel` is the previous panel, still carrying the tabs
            that have not been rebuilt -- saved, the advisor, the profile and
            the listing edit form, which the design package has no screen for.
            The panel goes when they do. */}
        <Route path="account" element={<ShellV3><DashboardV2 /></ShellV3>} />
        <Route path="account/listings" element={<ShellV3><MyListingsV3 /></ShellV3>} />
        <Route path="account/inbox" element={<ShellV3><InboxV3 /></ShellV3>} />
        <Route path="account/profile" element={<ShellV3><AccountProfileV3 /></ShellV3>} />
        <Route path="u/:id" element={<ShellV3><PublicProfileV3 /></ShellV3>} />
        <Route path="account/panel" element={<Layout><Account /></Layout>} />
        <Route path="agent" element={<Layout><Agent /></Layout>} />
        <Route path="checkout" element={<Layout><Checkout /></Layout>} />
        <Route path="privacy" element={<Layout><Privacy /></Layout>} />
        {/* An unknown path says so. It used to redirect to "/", which told
            the visitor their broken link had worked. */}
        <Route path="*" element={<Layout><NotFound /></Layout>} />
      </Routes>
    </Suspense>
  );
}

/* One place turns "signed out" into "go to login, and come back after". */
function Shell() {
  const navigate = useNavigate();
  const location = useLocation();
  const onSignedOut = useCallback(() => {
    // The console is a separate surface with its own token. A member session
    // expiring in another tab must not throw an admin out of /admin and onto
    // the member login -- the two sessions are independent by design.
    if (location.pathname.startsWith("/admin")) return;
    setReturnPath(location.pathname + location.search);
    navigate("/login", { replace: true });
  }, [navigate, location]);

  return (
    <AuthProvider onSignedOut={onSignedOut}>
      <Routes>
        {/* The console is its own surface: no public header, footer or nav.
            It carries a separate admin token and must not look like the
            member area a visitor might have open in another tab. */}
        <Route path="/admin" element={<Suspense fallback={null}><Admin /></Suspense>} />
        {MARKET_SLUGS.map((slug) => (
          <Route key={slug} path={`/${slug}/*`} element={<SiteRoutes />} />
        ))}
        <Route path="*" element={<SiteRoutes />} />
      </Routes>
    </AuthProvider>
  );
}

/* THE ORDER OF THESE THREE IS LOAD-BEARING.
 *
 * MarketProvider is above LocaleProvider, which reads oddly until you see
 * why: since Bassem's copy ruling, the DICTIONARIES themselves depend on the
 * market. `site_texts` rows are per market AND per locale, and the provider
 * that holds the market has to be the outer one for the provider that holds
 * the locale to be able to fold them in.
 *
 * MarketProvider reads the URL, so it lives inside BrowserRouter. It fetches
 * both locales' overrides at once, so switching language does not wait on a
 * second request to see the words change.
 */
export function App() {
  return (
    <BrowserRouter>
      <MarketProvider>
        <LocaleProvider>
          <Shell />
        </LocaleProvider>
      </MarketProvider>
    </BrowserRouter>
  );
}
